import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import type { OrganisationRelation } from "@thalamos/haven-common";

import { ConfirmChangesDialog } from "./ConfirmChangesDialog.js";

/** A relationship about to be added or removed, read from the organisation's side. */
export type RelationshipChange = {
  action: "add" | "remove";
  relation: OrganisationRelation;
  otherName: string;
};

/** The two ends of a relationship, named by their part in it whichever side it was read from. */
type Ends =
  | { kind: "membership"; member: string; group: string }
  | { kind: "operation"; operator: string; operated: string };

const endsOf = ({
  relation,
  organisationName,
  otherName,
}: {
  relation: OrganisationRelation;
  organisationName: string;
  otherName: string;
}): Ends =>
  match(relation)
    .returnType<Ends>()
    .with("member_of", () => ({
      kind: "membership",
      member: organisationName,
      group: otherName,
    }))
    .with("has_member", () => ({
      kind: "membership",
      member: otherName,
      group: organisationName,
    }))
    .with("operates", () => ({
      kind: "operation",
      operator: organisationName,
      operated: otherName,
    }))
    .with("operated_by", () => ({
      kind: "operation",
      operator: otherName,
      operated: organisationName,
    }))
    .exhaustive();

/**
 * Confirms adding or removing a relationship between organisations, saying whether it
 * changes who can see or admin young people. A tick is asked for on a membership
 * change, since membership is what carries access up; Operates carries none.
 *
 * A `problem` of `""` is a failure with no reason to show.
 */
export const RelationshipChangeDialog = ({
  organisationName,
  change,
  saving,
  problem,
  onCancel,
  onConfirm,
}: {
  organisationName: string;
  change: RelationshipChange | null;
  saving: boolean;
  problem: string | null;
  onCancel: () => Promisable<void>;
  onConfirm: (change: RelationshipChange) => Promisable<void>;
}) => {
  const { t } = useTranslation();

  if (change === null) {
    return null;
  }

  const ends = endsOf({
    relation: change.relation,
    organisationName,
    otherName: change.otherName,
  });
  const key = "admin.organisation.relationships.confirm";

  const wording = match({ ends, action: change.action })
    .with({ ends: { kind: "membership" }, action: "add" }, ({ ends }) => ({
      effect: t(`${key}.joins`, ends),
      access: t(`${key}.gains`, ends),
      acknowledgement: t(`${key}.gainsAcknowledgement`, ends),
    }))
    .with({ ends: { kind: "membership" }, action: "remove" }, ({ ends }) => ({
      effect: t(`${key}.leaves`, ends),
      access: t(`${key}.loses`, ends),
      acknowledgement: t(`${key}.losesAcknowledgement`, ends),
    }))
    .with({ ends: { kind: "operation" }, action: "add" }, ({ ends }) => ({
      effect: t(`${key}.operatesAdded`, ends),
      access: t(`${key}.operatesAccess`),
      acknowledgement: undefined,
    }))
    .with({ ends: { kind: "operation" }, action: "remove" }, ({ ends }) => ({
      effect: t(`${key}.operatesRemoved`, ends),
      access: t(`${key}.operatesAccess`),
      acknowledgement: undefined,
    }))
    .exhaustive();

  const action = match(change.action)
    .with("add", () => ({
      title: t(`${key}.addTitle`),
      confirm: t(`${key}.add`),
    }))
    .with("remove", () => ({
      title: t(`${key}.removeTitle`),
      confirm: t(`${key}.remove`),
    }))
    .exhaustive();

  return (
    <ConfirmChangesDialog
      open
      title={action.title}
      confirmLabel={action.confirm}
      saving={saving}
      error={
        problem === ""
          ? t("admin.organisation.relationships.changeFailed")
          : problem
      }
      acknowledgement={wording.acknowledgement}
      onCancel={onCancel}
      onConfirm={() => onConfirm(change)}
    >
      <Typography variant="body2">{wording.effect}</Typography>
      <Typography
        variant="body2"
        sx={
          wording.acknowledgement === undefined
            ? { fontWeight: 600 }
            : {
                fontWeight: 600,
                borderLeft: 3,
                borderColor: "warning.main",
                pl: 1.5,
              }
        }
      >
        {wording.access}
      </Typography>
    </ConfirmChangesDialog>
  );
};
